import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../../components/Header";
import { SquareLoader } from "react-spinners";
import RoomDropdown from "../../components/RoomDropdown/RoomDropdown";
import { useTranslation } from "react-i18next";
import { getApiEndpoint } from "../../utils/apiConfig";
import axiosWithAuth from "../../utils/axiosWithAuth";

// Özel stiller - bileşen içinde uygulanacak
const customStyles = `
.dependent-material {
  background-color: #f8f9fa;
  border-left: 3px solid #dee2e6;
}

.dependent-prefix {
  color: #6c757d;
  font-weight: normal;
  margin-right: 5px;
}

.main-material {
  font-weight: 500;
}

.table-row {
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background-color: #f1f1f1;
}
`;

const AllMaterialsPrice = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  
  document.title = t("allMaterialsPrice.0");
  const headerTitle = t("allMaterialsPrice.1");
  
  // State tanımlamaları
  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [shareUrl, setShareUrl] = useState("");
  
  const token = localStorage.getItem("token");

  // Ana verileri ve paylaşım linkini almak için
  useEffect(() => {
    if (!token) {
      navigate("/signup");
      return;
    }

    if (!id) {
      setError("Hesablama ID'si tapılmadı");
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Try the breakdown-materials endpoint first since it seems to be working
        const materialEndpoint = getApiEndpoint(`repairs/breakdown-materials/${id}`);
        console.log(`Materyal məlumatları alınır: ${materialEndpoint}`);
        
        const response = await axiosWithAuth().get(materialEndpoint);
        
        if (!response.data || !response.data.success) {
          throw new Error("Məlumat formatı uyğun deyil");
        }
        
        // API response'dan gelen materyal verilerini işle
        const materialsData = response.data.data || {};
        
        // Property bilgilerini ayarla
        const propertyData = {
          propertyType: { 
            name: { text: materialsData.propertyName || "Mülk" } 
          },
          area: materialsData.propertyArea || 0,
          doorCount: materialsData.doorCount || 0,
          windowCount: materialsData.windowCount || 0,
          roomCount: materialsData.roomCount || 0
        };
        
          setProperty(propertyData);
          
        // Oda ve materyal bilgilerini işle
        if (materialsData.rooms && Array.isArray(materialsData.rooms)) {
          // Her odaya, dependentMaterials içeren roomComponents ekleyin
          const processedRooms = materialsData.rooms.map(room => {
            return {
              roomType: room.roomType || { id: room.id, name: room.name },
              area: room.area || 0,
              roomComponents: (room.materials || []).map(material => {
                return {
                  material: {
                    name: { text: material.name || material.materialName },
                    dependentMaterials: material.dependentMaterials || [],
                    unitOfMeasure: material.unitOfMeasure || "m2",
                    currency: material.currency || "AZN"
                  },
                  consumption: material.area || 0,
                  price: material.price || 0
                };
              }).filter(comp => 
                comp.material.dependentMaterials && 
                comp.material.dependentMaterials.length > 0
            )
            };
          });
          
          setRooms(processedRooms);
          
          // İlk odayı seç ve filtrele
          if (processedRooms.length > 0) {
            const firstRoom = processedRooms[0].roomType;
            setSelectedRoom(firstRoom);
            handleRoomFiltering(firstRoom, processedRooms);
          }
          }
          
        // Paylaşım linki
          try {
          const shareEndpoint = getApiEndpoint(`repairs/share/${id}`);
            const shareResponse = await axiosWithAuth().get(shareEndpoint);
            if (shareResponse.data) {
              setShareUrl(shareResponse.data);
            }
          } catch (shareError) {
          console.error("Paylaşma linki alına bilmədi:", shareError);
          // Don't show an error, just disable sharing functionality
          setShareUrl(null);
          }
          
          setIsLoading(false);
      } catch (error) {
        console.error("Məlumat alma xətası:", error);
        
        if (error.response && error.response.status === 404) {
          setError(`Bu ID (${id}) ilə qeydə alınmış hesablama tapılmadı`);
        } else if (error.response && error.response.status === 401) {
          localStorage.removeItem("token");
          navigate("/login");
          return;
        } else {
          setError(`Məlumat yüklənərkən xəta baş verdi: ${error.message}`);
        }
        
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, token, navigate]);

  // Geri butonu için navigasyon
  const backwardNavigate = id ? `/allmaterials/${id}` : '/';

  // Odaya göre filtreleme ve toplam fiyat hesaplama
  const handleRoomFiltering = useCallback((selectedRoom, roomsData = []) => {
    if (!selectedRoom) {
      setFilteredRooms([]);
      setTotalPrice(0);
      return;
    }
    
    // Seçilen odaya göre filtrele
      const filtered = roomsData.filter(
        room => room.roomType.id === selectedRoom.id
      );
    
      setFilteredRooms(filtered);

      // Toplam fiyatı hesapla
      const total = filtered.reduce((sum, room) => {
        return sum + room.roomComponents.reduce((componentSum, component) => {
        // Ana materyal fiyatı
        const componentPrice = component.price || 0;
        
        // Bağımlı materyallerin fiyatları
        const dependentPrice = component.material.dependentMaterials
          ? component.material.dependentMaterials.reduce((depSum, dep) => {
              return depSum + ((dep.price || 0) * (component.consumption || 1));
            }, 0)
          : 0;
          
        return componentSum + componentPrice + dependentPrice;
        }, 0);
      }, 0);
      
      setTotalPrice(total);
  }, []);

  const handleRoomChange = (selectedRoom) => {
    setSelectedRoom(selectedRoom);
    handleRoomFiltering(selectedRoom);
  };

  // Birim etiketi dönüştürme
  const getUnitLabel = (unit) => {
    const unitMap = {
      'm2': 'm²',
      'm3': 'm³',
      'lm': 'm',
      'pcs': 'ədəd',
      'set': 'dəst',
      'lt': 'lt'
    };
    
    return unitMap[unit] || unit;
  };

  // Paylaşım linkini kopyalama
  const copyToClipboard = () => {
    if (!shareUrl) {
      console.error("Paylaşma URL'i mövcud deyil");
      return;
    }
    
    try {
      // Tam URL'i oluştur
      const fullUrl = window.location.origin + 
        getApiEndpoint(`repairs/shared-repair/${shareUrl.split('/').slice(-1)[0]}`).replace('/api/v1/e-prorab', '');
      
      navigator.clipboard.writeText(fullUrl)
        .then(() => {
          alert("Bağlantı panoya kopyalandı!");
        })
        .catch(err => {
          console.error("Kopyalama xətası:", err);
          alert("Bağlantı kopyalana bilmədi. Zəhmət olmasa, əl ilə kopyalayın.");
        });
    } catch (error) {
      console.error("Paylaşma URL'i işlənərkən xəta:", error);
    }
  };

  // Yükleme durumu
  if (isLoading) {
    return (
      <div className="loader-container">
        <SquareLoader color="#cda742" size={80} />
      </div>
    );
  }

  // Hata durumu
  if (error) {
    return (
      <div className="error-container">
        <Header
          title={headerTitle}
          showProfileIcon={true}
          showBackIcon={true}
          onBackClick={() => navigate(backwardNavigate)}
        />
        <div className="alert alert-danger m-3">
          {error}
        </div>
        <div className="d-flex justify-content-center m-3">
          <Link to="/" className="btn py-2 global-button">
            Ana səhifəyə qayıt
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="allmaterialsprice-section">
      {/* Özel CSS stilleri */}
      <style>{customStyles}</style>
      
      <Header
        title={headerTitle}
        showProfileIcon={true}
        showBackIcon={true}
        onBackClick={() => navigate(backwardNavigate)}
      />
      
      {property && (
        <div className="property-header">
          <div className="property-title">
            <h2>{property.propertyType?.name?.text || "Mülk"}</h2>
            <span className="property-area">{property.area} m²</span>
          </div>
          <div className="property-details">
            <span className="detail-item">
              <i className="fas fa-door-open"></i> {property.doorCount || 0}
            </span>
            <span className="detail-item">
              <i className="fas fa-window-maximize"></i> {property.windowCount || 0}
            </span>
            <span className="detail-item">
              <i className="fas fa-th-large"></i> {property.roomCount || 0}
            </span>
          </div>
        </div>
      )}
      
      <div className="materials-content">
        <div className="top-section">
          <h5>{t("allMaterialsPrice.0")}</h5>
          <p className="subtitle">{t("allMaterialsPrice.1")}</p>
        </div>
        
        <div className="middle-section">
          {rooms.length > 0 ? (
            <RoomDropdown
              onChange={handleRoomChange}
              selectedRoomTypes={rooms.map(room => room.roomType)}
              selectedValue={selectedRoom}
            />
          ) : (
            <div className="no-rooms-message">
              {t("allMaterialsPrice.8")}
            </div>
          )}
        </div>
        
        <div className="bottom-section">
          {filteredRooms.length > 0 ? (
            <>
              <div className="materials-table">
                <div className="table-header">
                  <div className="column name-column">{t("allMaterialsPrice.9")}</div>
                  <div className="column amount-column">{t("allMaterialsPrice.10")}</div>
                  <div className="column price-column">{t("allMaterialsPrice.11")}</div>
                </div>
                <div className="table-body">
                  {filteredRooms.map((room, roomIndex) => (
                    room.roomComponents.map((component, compIndex) => {
                      // Her bileşen için ana materyal satırı
                      return (
                        <React.Fragment key={`${roomIndex}-${compIndex}`}>
                          {/* Ana materyal satırı */}
                          <div className="table-row main-material">
                          <div className="column name-column">
                              {typeof component.material.name === 'object' 
                                ? component.material.name.text 
                                : component.material.name || t("allMaterialsPrice.12")}
                          </div>
                          <div className="column amount-column">
                            {component.consumption || 0} {
                                getUnitLabel(component.material.unitOfMeasure || "m2")
                            }
                          </div>
                          <div className="column price-column">
                            {Math.round(component.price || 0)} {
                                component.material.currency || "AZN"
                              }
                            </div>
                          </div>
                          
                          {/* Bağımlı materyaller */}
                          {component.material.dependentMaterials && component.material.dependentMaterials.length > 0 && 
                            component.material.dependentMaterials.map((depMaterial, depIndex) => (
                              <div className="table-row dependent-material" key={`${roomIndex}-${compIndex}-dep-${depIndex}`}>
                                <div className="column name-column">
                                  <span className="dependent-prefix">└─ </span>
                                  {typeof depMaterial.name === 'object' 
                                    ? depMaterial.name.text 
                                    : depMaterial.name || t("allMaterialsPrice.12")}
                                </div>
                                <div className="column amount-column">
                                  {component.consumption || 0} {
                                    getUnitLabel(depMaterial.unitOfMeasure || component.material.unitOfMeasure || "m2")
                                  }
                                </div>
                                <div className="column price-column">
                                  {Math.round((depMaterial.price || 0) * (component.consumption || 1))} {
                                    depMaterial.currency || component.material.currency || "AZN"
                            }
                          </div>
                        </div>
                            ))
                          }
                        </React.Fragment>
                      );
                    })
                  ))}
                </div>
              </div>
              
              <div className="summary-table">
                <div className="summary-row">
                  <div className="column name-column">{t("allMaterialsPrice.2")}</div>
                  <div className="column amount-column">{property?.area || 0} m²</div>
                  <div className="column price-column">
                    {Math.round(totalPrice)} AZN
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="no-materials">
              {t("allMaterialsPrice.13")}
            </div>
          )}
        </div>
      </div>

      <div className="button-section">
        <Link to={`/shops/${id}`}>
          <button className="btn py-3 global-button">
            {t("allMaterialsPrice.3")}
          </button>
        </Link>
        
        {shareUrl && (
        <button
          className="btn py-3 share-button mt-2"
          data-bs-toggle="modal"
          data-bs-target="#shareFriendModal"
        >
          <i className="fas fa-share-alt me-2"></i>
          {t("allMaterialsPrice.4")}
        </button>
        )}
      </div>

      {/* Paylaşım Modal */}
      <div
        className="modal fade"
        id="shareFriendModal"
        tabIndex="-1"
        aria-labelledby="shareFriendModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{t("allMaterialsPrice.5")}</h5>
              <button 
                type="button" 
                className="btn-close" 
                data-bs-dismiss="modal" 
                aria-label="Close"
              ></button>
            </div>
            
            <div className="modal-body">
              <h6>{t("allMaterialsPrice.6")}</h6>
              
              <div className="social-sharing">
                {shareUrl && (
                  <>
                    <a
                      href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-icon whatsapp"
                    >
                      <i className="fab fa-whatsapp"></i>
                    </a>
                    <a
                      href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-icon telegram"
                    >
                      <i className="fab fa-telegram"></i>
                    </a>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-icon facebook"
                    >
                      <i className="fab fa-facebook-f"></i>
                    </a>
                  </>
                )}
              </div>
              
              <h6 className="mt-4">{t("allMaterialsPrice.7")}</h6>
              <div className="url-copy-container">
                <div className="url-display">
                  {shareUrl && (
                    shareUrl.length > 30 ? 
                      `${shareUrl.slice(0, 30)}...` : 
                      shareUrl
                  )}
                </div>
                <button 
                  className="copy-button" 
                  onClick={copyToClipboard}
                  title={t("allMaterialsPrice.15")}
                >
                  <i className="fas fa-copy"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllMaterialsPrice;
